/**
 * @jsxRuntime classic
 * @jsx jsx
 */
import { type MouseEvent, type PropsWithChildren, useCallback, useEffect, useState } from 'react';

import debounce from 'lodash/debounce';
import { useIntl } from 'react-intl';

import { cssMap, cx, jsx } from '@atlaskit/css';
import EditorExpandIcon from '@atlaskit/icon/core/grow-horizontal';
import EditorCollapseIcon from '@atlaskit/icon/core/shrink-horizontal';
import { Box, Inline } from '@atlaskit/primitives/compiled';
import { token } from '@atlaskit/tokens';
import Tooltip from '@atlaskit/tooltip';

import { Blur } from './blur';
import { ButtonIcon } from './button-icon';
import { messages } from './messages';
import { ToggleButton } from './toggle-button';

const styles = cssMap({
	container: {
		overflow: 'hidden',
		position: 'relative',
	},
	expanded: {
		height: 'auto',
	},
	collapsed: {
		height: '150px',
	},
	pointerCursor: {
		cursor: 'pointer',
	},
	titleContainer: {
		color: token('color.text.subtlest'),
		font: token('font.body.small'),
		fontWeight: token('font.weight.semibold'),
		paddingTop: token('space.200'),
		paddingRight: token('space.300'),
		paddingBottom: token('space.0'),
		paddingLeft: token('space.300'),
	},
	title: {
		alignContent: 'center',
		height: '26px',
	},
});

type CollapseProps = {
	collapsed: boolean;
	isLoading: boolean;
	toggleCollapsedState?: () => void;
	expandWhenClickedAnywhere?: boolean;
	isStreaming?: boolean;
	disableCollapse?: boolean;
	callbacks?: {
		// called when card is rendered in the expanded state due to the answer being short
		onExpandedByDefault?: () => void;
		onExpandClicked?: () => void;
		onCollapseClicked?: () => void;
	};
};

enum EXPANSION_STATE {
	LOADING = 1, // still computing if the threshold is crossed
	COLLAPSED = 2, // threshold crossed, render component collapsed
	EXPANDED = 3, // below threshold, render exanded without collapse option
}

export const Collapse: ({
	children,
	collapsed,
	isLoading,
	toggleCollapsedState,
	expandWhenClickedAnywhere,
	isStreaming,
	disableCollapse,
	callbacks,
}: PropsWithChildren<CollapseProps>) => JSX.Element = ({
	children,
	collapsed,
	isLoading,
	toggleCollapsedState = () => {},
	expandWhenClickedAnywhere = false,
	isStreaming = false,
	disableCollapse = false,
	callbacks,
}: PropsWithChildren<CollapseProps>) => {
	const [initialExpansionState, setInitialExpansionState] = useState<EXPANSION_STATE>(
		EXPANSION_STATE.LOADING,
	);
	const threshold = 150;

	const collapseableContainerRef = useCallback(
		(node: HTMLInputElement | null) => {
			if (!node || disableCollapse) {
				return;
			}

			const resizeObserver = new ResizeObserver(
				debounce(() => {
					// delay computation to ensure the observed node has rendered and the height reported is accurate
					setTimeout(() => {
						const childHeight = node.getBoundingClientRect().height;
						if (isStreaming || isLoading || !Boolean(childHeight)) {
							return;
						}

						// checking if the length of the answer is smaller than the collapse threshold
						if (childHeight <= threshold && initialExpansionState !== EXPANSION_STATE.EXPANDED) {
							setInitialExpansionState(EXPANSION_STATE.EXPANDED);
						} else {
							setInitialExpansionState(EXPANSION_STATE.COLLAPSED);
						}
					}, 100);
				}, 100),
			);

			resizeObserver.observe(node);
		},
		// make sure the dependancy array is empty,
		// this is to ensure the resizeObserver is registered just once on mount
		// eslint-disable-next-line react-hooks/exhaustive-deps
		[],
	);

	useEffect(() => {
		/**
		 * if the answer has finished isLoading and the container is too small to collapse,
		 * toggle collapsed state to false
		 * this helps external components watching the boolean to know the actual state
		 */
		if (
			(initialExpansionState === EXPANSION_STATE.EXPANDED &&
				!isLoading &&
				!isStreaming &&
				collapsed) ||
			disableCollapse
		) {
			toggleCollapsedState();
			callbacks?.onExpandedByDefault?.();
		}
	}, [
		initialExpansionState,
		isLoading,
		isStreaming,
		collapsed,
		callbacks,
		toggleCollapsedState,
		disableCollapse,
	]);

	const { formatMessage } = useIntl();
	const tooltipText = formatMessage(
		collapsed ? messages.ai_answer_expand_tooltip : messages.ai_answer_collapse_tooltip,
	);

	// allow whole container clicks to expand when enabled
	const onContainerClickHandler = () => {
		if (expandWhenClickedAnywhere && collapsed) {
			callbacks?.onExpandClicked?.();
			toggleCollapsedState();
		}
	};

	const onClickHandler = (e: MouseEvent<HTMLElement>) => {
		// stop container click from being called
		e.stopPropagation();

		if (collapsed) {
			callbacks?.onExpandClicked?.();
		} else {
			callbacks?.onCollapseClicked?.();
		}
		toggleCollapsedState();
	};

	/**
	 * render the non collapsable version
	 * - child container's height is lesser than the threshold
	 * - collapsed card is externally disabled, ex: for a person card
	 */
	if (initialExpansionState === EXPANSION_STATE.EXPANDED || disableCollapse) {
		return (
			<Box xcss={cx(styles.container, styles.expanded)}>
				<div>
					{!isLoading && (
						<Inline alignBlock="center" xcss={styles.titleContainer}>
							<Box xcss={styles.title}>{formatMessage(messages.title_label)}</Box>
						</Inline>
					)}
					{children}
				</div>
			</Box>
		);
	}

	return (
		// eslint-disable-next-line @atlassian/a11y/interactive-element-not-keyboard-focusable
		<Box
			xcss={cx(
				styles.container,
				collapsed ? styles.collapsed : styles.expanded,
				expandWhenClickedAnywhere && collapsed && styles.pointerCursor,
			)}
			onClick={onContainerClickHandler}
		>
			<Inline alignBlock="center" spread={'space-between'} xcss={styles.titleContainer}>
				<Box xcss={styles.title}>{formatMessage(messages.title_label)}</Box>
				{initialExpansionState === EXPANSION_STATE.COLLAPSED && (
					<Tooltip position={'bottom'} content={tooltipText}>
						<ToggleButton onClick={onClickHandler} data-testid="expand-collapse-toggle">
							<ButtonIcon>
								{collapsed ? (
									<EditorExpandIcon
										color="currentColor"
										spacing="spacious"
										label={formatMessage(messages.expand_label)}
									/>
								) : (
									<EditorCollapseIcon
										color="currentColor"
										spacing="spacious"
										label={formatMessage(messages.collapse_label)}
									/>
								)}
							</ButtonIcon>
						</ToggleButton>
					</Tooltip>
				)}
			</Inline>
			<div ref={collapseableContainerRef}>{children}</div>
			{initialExpansionState === EXPANSION_STATE.COLLAPSED && collapsed && <Blur></Blur>}
		</Box>
	);
};
